/**
 * NotificationSetup.tsx — Settings card for push & Telegram notification configuration.
 * Shows Web Push subscription controls, Telegram bot setup, and notification preferences.
 */

import { useState, useEffect } from 'react'
import { usePushNotifications } from '../hooks/useNotifications'
import { API_BASE } from '../utils/api'

// ─── Types ────────────────────────────────────────────────────────────────────

interface NotifPrefs {
  circuit_breaker: boolean
  stop_loss: boolean
  reentry: boolean
  eod_squareoff: boolean
  vps_offline: boolean
}

const DEFAULT_PREFS: NotifPrefs = {
  circuit_breaker: true,
  stop_loss: true,
  reentry: false,
  eod_squareoff: true,
  vps_offline: true,
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function PermissionBadge({ permission }: { permission: NotificationPermission }) {
  const configs: Record<NotificationPermission, { label: string; color: string; bg: string; border: string }> = {
    granted: {
      label: 'Allowed',
      color: '#4ade80',
      bg: 'rgba(74,222,128,0.1)',
      border: 'rgba(74,222,128,0.25)',
    },
    denied: {
      label: 'Blocked',
      color: '#f87171',
      bg: 'rgba(248,113,113,0.1)',
      border: 'rgba(248,113,113,0.25)',
    },
    default: {
      label: 'Not set',
      color: '#94a3b8',
      bg: 'rgba(148,163,184,0.1)',
      border: 'rgba(148,163,184,0.2)',
    },
  }
  const cfg = configs[permission]
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.3rem',
        fontSize: '0.625rem',
        fontWeight: 800,
        letterSpacing: '0.06em',
        color: cfg.color,
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        borderRadius: '3px',
        padding: '0.1rem 0.45rem',
        textTransform: 'uppercase',
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: '50%',
          background: cfg.color,
          display: 'inline-block',
        }}
      />
      {cfg.label}
    </span>
  )
}

function CheckboxRow({
  id,
  label,
  description,
  checked,
  onChange,
}: {
  id: string
  label: string
  description: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label
      htmlFor={id}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem',
        padding: '0.625rem 0',
        borderBottom: '1px solid var(--border)',
        cursor: 'pointer',
        transition: 'background 0.15s',
      }}
    >
      {/* Custom checkbox */}
      <div style={{ position: 'relative', flexShrink: 0, marginTop: '0.125rem' }}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
        />
        <div
          style={{
            width: 16,
            height: 16,
            borderRadius: '4px',
            border: checked ? '2px solid var(--accent)' : '2px solid var(--border)',
            background: checked ? 'var(--accent)' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.15s',
          }}
        >
          {checked && (
            <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
              <path d="M1 3.5L3.5 6L8 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>
      </div>
      <div>
        <div style={{ fontSize: '0.8125rem', color: 'var(--text-primary)', fontWeight: 500, lineHeight: 1.3 }}>
          {label}
        </div>
        <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', lineHeight: 1.4, marginTop: '0.15rem' }}>
          {description}
        </div>
      </div>
    </label>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface NotificationSetupProps {
  /** Pass true for admin users to show the VPS offline preference */
  isAdmin?: boolean
}

export function NotificationSetup({ isAdmin = false }: NotificationSetupProps) {
  const { supported, permission, subscribed, loading, subscribe, unsubscribe } = usePushNotifications()

  // ── Telegram state ────────────────────────────────────────────────────────
  const [telegramChatId, setTelegramChatId] = useState('')
  const [savedChatId, setSavedChatId] = useState<string | null>(null)
  const [telegramLoading, setTelegramLoading] = useState(false)
  const [testingTelegram, setTestingTelegram] = useState(false)

  // ── Push action states ────────────────────────────────────────────────────
  const [pushActionLoading, setPushActionLoading] = useState(false)
  const [pushError, setPushError] = useState<string | null>(null)

  // ── Preferences state ─────────────────────────────────────────────────────
  const [prefs, setPrefs] = useState<NotifPrefs>(DEFAULT_PREFS)
  const [prefsDirty, setPrefsDirty] = useState(false)
  const [savingPrefs, setSavingPrefs] = useState(false)

  // ── Load saved settings from backend ─────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('lamoney-token')
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`

    fetch(`${API_BASE}/api/users/me/notifications`, { headers })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (!data) return
        if (data.telegram_chat_id) setSavedChatId(data.telegram_chat_id)
        if (data.prefs) setPrefs({ ...DEFAULT_PREFS, ...data.prefs })
      })
      .catch(() => {/* best-effort */})
  }, [])

  // ── Push handlers ─────────────────────────────────────────────────────────
  const handleSubscribe = async () => {
    setPushActionLoading(true)
    setPushError(null)
    try {
      await subscribe()
    } catch (err: any) {
      setPushError(err?.message || 'Failed to enable notifications')
    } finally {
      setPushActionLoading(false)
    }
  }

  const handleUnsubscribe = async () => {
    setPushActionLoading(true)
    setPushError(null)
    try {
      await unsubscribe()
    } catch (err: any) {
      setPushError(err?.message || 'Failed to disable notifications')
    } finally {
      setPushActionLoading(false)
    }
  }

  // ── Telegram handlers ─────────────────────────────────────────────────────
  const handleSaveTelegram = async () => {
    if (!telegramChatId.trim()) return
    setTelegramLoading(true)
    try {
      const token = localStorage.getItem('lamoney-token')
      const res = await fetch(`${API_BASE}/api/users/me/notifications`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ telegram_chat_id: telegramChatId.trim() }),
      })
      if (res.ok) {
        setSavedChatId(telegramChatId.trim())
        setTelegramChatId('')
      }
    } catch {
      /* ignore */
    } finally {
      setTelegramLoading(false)
    }
  }

  const handleTestTelegram = async () => {
    setTestingTelegram(true)
    try {
      const token = localStorage.getItem('lamoney-token')
      await fetch(`${API_BASE}/api/notify/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ channel: 'telegram' }),
      })
    } catch {
      /* ignore */
    } finally {
      setTestingTelegram(false)
    }
  }

  // ── Preferences handlers ──────────────────────────────────────────────────
  const updatePref = (key: keyof NotifPrefs, value: boolean) => {
    setPrefs((prev) => ({ ...prev, [key]: value }))
    setPrefsDirty(true)
  }

  const handleSavePrefs = async () => {
    setSavingPrefs(true)
    try {
      const token = localStorage.getItem('lamoney-token')
      await fetch(`${API_BASE}/api/users/me/notifications`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ prefs }),
      })
      setPrefsDirty(false)
    } catch {
      /* ignore */
    } finally {
      setSavingPrefs(false)
    }
  }

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="card" id="notification-setup-card">
      {/* ── Card Header ────────────────────────────────────────────────── */}
      <div className="card-header">
        <span className="card-title">🔔 Notification Channels</span>
        <span
          style={{
            fontSize: '0.5625rem',
            fontWeight: 800,
            letterSpacing: '0.06em',
            color: 'var(--accent)',
            background: 'rgba(56,189,248,0.08)',
            border: '1px solid rgba(56,189,248,0.2)',
            borderRadius: '3px',
            padding: '0.1rem 0.45rem',
          }}
        >
          ALERTS
        </span>
      </div>

      <div className="cfg-grid" style={{ padding: '0 1rem 1.25rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>

        {/* ════════════════════════════════════════════════════════════════
            Section A: Web Push
        ════════════════════════════════════════════════════════════════ */}
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '0.875rem',
            }}
          >
            <span
              style={{
                fontSize: '0.75rem',
                width: 26,
                height: 26,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(100,116,139,0.12)',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
              }}
            >
              🌐
            </span>
            <span
              style={{
                fontSize: '0.875rem',
                fontWeight: 700,
                color: 'var(--text-primary)',
                letterSpacing: '-0.01em',
              }}
            >
              Browser Push Notifications
            </span>
            {supported && <PermissionBadge permission={permission} />}
          </div>

          {!supported ? (
            <div
              style={{
                padding: '0.75rem 1rem',
                background: 'rgba(100,116,139,0.08)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.8125rem',
                color: 'var(--text-muted)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <span>⚠</span>
              <span>Your browser does not support Web Push notifications.</span>
            </div>
          ) : permission === 'denied' ? (
            <div
              style={{
                padding: '0.875rem 1rem',
                background: 'rgba(248,113,113,0.06)',
                border: '1px solid rgba(248,113,113,0.2)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.8125rem',
                color: '#f87171',
                lineHeight: 1.5,
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.625rem',
              }}
            >
              <span style={{ flexShrink: 0, marginTop: 1 }}>🚫</span>
              <div>
                <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Notifications blocked</div>
                <div style={{ color: 'rgba(248,113,113,0.75)', fontSize: '0.75rem' }}>
                  To receive alerts, go to your browser settings and allow notifications for this site, then reload the page.
                </div>
              </div>
            </div>
          ) : subscribed ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.875rem 1rem',
                background: 'rgba(74,222,128,0.05)',
                border: '1px solid rgba(74,222,128,0.18)',
                borderRadius: 'var(--radius-sm)',
                gap: '1rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                <span style={{ fontSize: '1rem' }}>✅</span>
                <div>
                  <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#4ade80' }}>
                    Browser notifications active
                  </div>
                  <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                    You'll receive real-time alerts in this browser
                  </div>
                </div>
              </div>
              <button
                id="push-disable-btn"
                className="btn btn-ghost btn-sm"
                onClick={handleUnsubscribe}
                disabled={pushActionLoading || loading}
                style={{ flexShrink: 0, minWidth: 80 }}
              >
                {pushActionLoading ? '⟳' : 'Disable'}
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
              <p
                style={{
                  fontSize: '0.75rem',
                  color: 'var(--text-muted)',
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                Get instant browser alerts when circuit breakers trigger, stop losses hit, or critical system events occur — even when Aalap is in the background.
              </p>
              <button
                id="push-enable-btn"
                className="btn btn-primary btn-sm"
                onClick={handleSubscribe}
                disabled={pushActionLoading || loading}
                style={{ alignSelf: 'flex-start', minWidth: 220 }}
              >
                {pushActionLoading || loading ? '⟳ Setting up…' : '🔔 Enable Browser Notifications'}
              </button>
              {pushError && (
                <p style={{ fontSize: '0.6875rem', color: '#f87171', margin: 0 }}>
                  ⚠ {pushError}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Removed Divider */}

        {/* ════════════════════════════════════════════════════════════════
            Section B: Telegram
        ════════════════════════════════════════════════════════════════ */}
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '0.875rem',
            }}
          >
            <span
              style={{
                fontSize: '0.75rem',
                width: 26,
                height: 26,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(100,116,139,0.12)',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
              }}
            >
              ✈️
            </span>
            <span
              style={{
                fontSize: '0.875rem',
                fontWeight: 700,
                color: 'var(--text-primary)',
                letterSpacing: '-0.01em',
              }}
            >
              Telegram Alerts
            </span>
            {savedChatId && (
              <span
                style={{
                  fontSize: '0.625rem',
                  fontWeight: 800,
                  letterSpacing: '0.06em',
                  color: '#4ade80',
                  background: 'rgba(74,222,128,0.1)',
                  border: '1px solid rgba(74,222,128,0.25)',
                  borderRadius: '3px',
                  padding: '0.1rem 0.45rem',
                  textTransform: 'uppercase',
                }}
              >
                CONNECTED
              </span>
            )}
          </div>

          {/* Bot instructions */}
          <div
            style={{
              padding: '0.75rem 1rem',
              background: 'rgba(56,189,248,0.05)',
              border: '1px solid rgba(56,189,248,0.15)',
              borderRadius: 'var(--radius-sm)',
              marginBottom: '0.875rem',
              fontSize: '0.8125rem',
              color: 'var(--text-secondary)',
              lineHeight: 1.6,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: '0.25rem', color: 'var(--text-primary)' }}>
              Setup instructions:
            </div>
            <ol style={{ margin: 0, paddingLeft: '1.25rem' }}>
              <li>
                Open Telegram and search for{' '}
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    color: 'var(--accent)',
                    fontWeight: 600,
                  }}
                >
                  @LaMoney_Alerts_bot
                </span>
              </li>
              <li>
                Send the command{' '}
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    color: 'var(--accent)',
                    fontWeight: 600,
                    background: 'rgba(56,189,248,0.08)',
                    padding: '0 0.3rem',
                    borderRadius: '3px',
                  }}
                >
                  /start
                </span>
              </li>
              <li>Copy your <strong>Chat ID</strong> from the bot's reply and paste it below</li>
            </ol>
          </div>

          {/* Saved chat ID display */}
          {savedChatId && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.625rem 0.875rem',
                background: 'rgba(74,222,128,0.05)',
                border: '1px solid rgba(74,222,128,0.15)',
                borderRadius: 'var(--radius-sm)',
                marginBottom: '0.75rem',
                gap: '0.75rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.75rem' }}>💬</span>
                <div>
                  <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>Saved Chat ID</div>
                  <div
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: '0.8125rem',
                      fontWeight: 700,
                      color: 'var(--text-primary)',
                      letterSpacing: '0.02em',
                    }}
                  >
                    {savedChatId}
                  </div>
                </div>
              </div>
              <button
                id="telegram-test-btn"
                className="btn btn-ghost btn-sm"
                onClick={handleTestTelegram}
                disabled={testingTelegram}
                style={{ flexShrink: 0 }}
              >
                {testingTelegram ? '⟳ Sending…' : '⚡ Test'}
              </button>
            </div>
          )}

          {/* Chat ID input */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <label
                htmlFor="telegram-chat-id-input"
                style={{
                  display: 'block',
                  fontSize: '0.6875rem',
                  color: 'var(--text-muted)',
                  fontWeight: 600,
                  marginBottom: '0.375rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {savedChatId ? 'Update Chat ID' : 'Your Telegram Chat ID'}
              </label>
              <input
                id="telegram-chat-id-input"
                className="cfg-input"
                type="text"
                inputMode="numeric"
                placeholder="e.g. 123456789"
                value={telegramChatId}
                onChange={(e) => setTelegramChatId(e.target.value.replace(/\D/g, ''))}
                style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.05em' }}
              />
            </div>
            <button
              id="telegram-save-btn"
              className="btn btn-primary btn-sm"
              onClick={handleSaveTelegram}
              disabled={!telegramChatId.trim() || telegramLoading}
              style={{ minWidth: 70, marginBottom: 1 }}
            >
              {telegramLoading ? '⟳' : 'Save'}
            </button>
          </div>
        </div>

        {/* Removed Divider */}

        {/* ════════════════════════════════════════════════════════════════
            Section C: Notification Preferences
        ════════════════════════════════════════════════════════════════ */}
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '0.875rem',
            }}
          >
            <span
              style={{
                fontSize: '0.75rem',
                width: 26,
                height: 26,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(100,116,139,0.12)',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
              }}
            >
              ⚙
            </span>
            <span
              style={{
                fontSize: '0.875rem',
                fontWeight: 700,
                color: 'var(--text-primary)',
                letterSpacing: '-0.01em',
              }}
            >
              Alert Preferences
            </span>
          </div>

          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0 0 0.875rem', lineHeight: 1.5 }}>
            Choose which events trigger notifications across all your enabled channels.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <CheckboxRow
              id="pref-circuit-breaker"
              label="Circuit Breaker Triggered"
              description="Fired when global or per-set MTM loss threshold is breached and trading halts."
              checked={prefs.circuit_breaker}
              onChange={(v) => updatePref('circuit_breaker', v)}
            />
            <CheckboxRow
              id="pref-stop-loss"
              label="Stop Loss Hit"
              description="Alert when an individual position or strategy reaches its stop loss target."
              checked={prefs.stop_loss}
              onChange={(v) => updatePref('stop_loss', v)}
            />
            <CheckboxRow
              id="pref-reentry"
              label="Re-entry Placed"
              description="Notify when a re-entry order is auto-placed after a stop loss exit."
              checked={prefs.reentry}
              onChange={(v) => updatePref('reentry', v)}
            />
            <CheckboxRow
              id="pref-eod-squareoff"
              label="End-of-Day Square-off"
              description="Alert when all positions are squared off at market close time."
              checked={prefs.eod_squareoff}
              onChange={(v) => updatePref('eod_squareoff', v)}
            />
            {isAdmin && (
              <CheckboxRow
                id="pref-vps-offline"
                label="VPS Offline"
                description="Admin-only: Alert when the trading server stops sending heartbeats."
                checked={prefs.vps_offline}
                onChange={(v) => updatePref('vps_offline', v)}
              />
            )}
          </div>

          {/* Save prefs */}
          <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              id="save-prefs-btn"
              className="btn btn-primary btn-sm"
              onClick={handleSavePrefs}
              disabled={!prefsDirty || savingPrefs}
              style={{ minWidth: 150 }}
            >
              {savingPrefs ? '⟳ Saving…' : '✓ Save Preferences'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
